/**
 * Hustler theme — global behaviours.
 * Dialog open/close, quantity steppers, toast, predictive search, details animation.
 * Section-specific behaviour lives in each section's {% javascript %} tag.
 */
(function () {
  'use strict';

  const theme = (window.theme = window.theme || {});

  /* ---------------------------------------------------------------------
     Helpers
     --------------------------------------------------------------------- */
  theme.debounce = function (fn, wait) {
    let t;
    return function () {
      const args = arguments;
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  };

  theme.formatMoney = function (cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    let value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format || theme.moneyFormat || '${{amount}}';

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number == null) return 0;
      number = (number / 100.0).toFixed(precision);
      const parts = number.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }

    const match = formatString.match(placeholderRegex);
    switch (match ? match[1] : 'amount') {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return formatString.replace(placeholderRegex, value);
  };

  /* ---------------------------------------------------------------------
     Toast
     --------------------------------------------------------------------- */
  let toastTimer;
  theme.toast = function (message, duration) {
    const el = document.getElementById('Toast');
    if (!el) return;
    el.textContent = message;
    el.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('is-visible'), duration || 2600);
  };

  /* ---------------------------------------------------------------------
     Dialogs (drawers, modals)
     --------------------------------------------------------------------- */
  theme.openDialog = function (id) {
    const dialog = typeof id === 'string' ? document.getElementById(id) : id;
    if (!dialog || dialog.open) return;
    dialog.__returnFocus = document.activeElement;
    dialog.showModal();
    const focusTarget = dialog.querySelector('[autofocus], input, button, a');
    if (focusTarget) focusTarget.focus({ preventScroll: true });
    dialog.dispatchEvent(new CustomEvent('dialog:open'));
  };

  theme.closeDialog = function (dialog) {
    if (!dialog || !dialog.open) return;
    dialog.close();
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-open-dialog]');
    if (opener) {
      event.preventDefault();
      theme.openDialog(opener.getAttribute('data-open-dialog'));
      return;
    }
    const closer = event.target.closest('[data-close-dialog]');
    if (closer) {
      event.preventDefault();
      theme.closeDialog(closer.closest('dialog'));
      return;
    }
    // Click on the backdrop closes the dialog.
    if (event.target instanceof HTMLDialogElement && event.target.open) {
      const rect = event.target.getBoundingClientRect();
      const inside =
        event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!inside) theme.closeDialog(event.target);
    }
  });

  document.addEventListener(
    'close',
    (event) => {
      const dialog = event.target;
      if (!(dialog instanceof HTMLDialogElement)) return;
      if (dialog.__returnFocus && typeof dialog.__returnFocus.focus === 'function') {
        dialog.__returnFocus.focus({ preventScroll: true });
      }
      dialog.dispatchEvent(new CustomEvent('dialog:close'));
    },
    true
  );

  /* ---------------------------------------------------------------------
     Quantity stepper: <div class="quantity" data-quantity> with +/- buttons
     --------------------------------------------------------------------- */
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-quantity-change]');
    if (!btn) return;
    const wrapper = btn.closest('[data-quantity]');
    const input = wrapper && wrapper.querySelector('input');
    if (!input) return;
    const step = parseInt(input.step || '1', 10) || 1;
    const min = parseInt(input.min || '0', 10);
    const max = input.max ? parseInt(input.max, 10) : Infinity;
    const delta = btn.getAttribute('data-quantity-change') === 'up' ? step : -step;
    const next = Math.min(max, Math.max(min, (parseInt(input.value, 10) || 0) + delta));
    if (next !== parseInt(input.value, 10)) {
      input.value = String(next);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });

  /* ---------------------------------------------------------------------
     Predictive search inside the search drawer
     --------------------------------------------------------------------- */
  const searchInput = document.querySelector('[data-predictive-input]');
  const searchResults = document.querySelector('[data-predictive-results]');
  if (searchInput && searchResults && theme.routes && theme.routes.predictiveSearch) {
    const fetchResults = theme.debounce(async () => {
      const q = searchInput.value.trim();
      if (q.length < 2) {
        searchResults.innerHTML = '';
        return;
      }
      try {
        const url = `${theme.routes.predictiveSearch}?q=${encodeURIComponent(q)}&resources[type]=product,article,page&resources[limit]=5&resources[options][unavailable_products]=last&section_id=predictive-search`;
        const res = await fetch(url);
        if (!res.ok) throw new Error(res.statusText);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const inner = doc.querySelector('#PredictiveSearchResults');
        searchResults.innerHTML = inner ? inner.innerHTML : '';
      } catch (e) {
        searchResults.innerHTML = '';
      }
    }, 250);
    searchInput.addEventListener('input', fetchResults);
  }

  /* ---------------------------------------------------------------------
     Section rendering helper for AJAX fetches of section markup
     --------------------------------------------------------------------- */
  theme.fetchSection = async function (sectionId, url) {
    const target = new URL(url || window.location.href, window.location.origin);
    target.searchParams.set('section_id', sectionId);
    const res = await fetch(target.toString());
    if (!res.ok) throw new Error(res.statusText);
    const html = await res.text();
    return new DOMParser().parseFromString(html, 'text/html');
  };

  /* ---------------------------------------------------------------------
     Reveal-on-scroll (subtle) for elements with [data-reveal]
     --------------------------------------------------------------------- */
  if ('IntersectionObserver' in window && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px' }
    );
    document.querySelectorAll('[data-reveal]').forEach((el) => io.observe(el));
  } else {
    document.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('is-revealed'));
  }

  /* Theme editor: re-open drawers when their section is selected. */
  document.addEventListener('shopify:section:select', (event) => {
    const dialog = event.target.querySelector('dialog');
    if (dialog && event.target.id.includes('cart')) theme.openDialog(dialog);
  });
})();
